import type { Scroller } from '../core/scroll'
import { store } from '../core/store'
import { $, $$ } from './dom'

/** Header chrome: scrolled/hidden states, anchor navigation, scroll-spy and the mobile menu. */
export class Header {
  private el = $('#site-header')
  private menu = $<HTMLDialogElement>('#menu')
  private menuBtn = $<HTMLButtonElement>('#menu-btn')
  private scrolled = false
  /** Header is permanently pinned and never auto-hides */
  pinned = true

  constructor(
    private readonly scroller: Scroller,
    private readonly getSectionStop?: (id: string) => number | undefined,
  ) {
    document.addEventListener('click', (event) => this.onAnchorClick(event))

    this.menuBtn.addEventListener('click', () => {
      this.scroller.lock()
      this.menu.showModal()
    })
    this.menu.addEventListener('close', () => this.scroller.unlock())
    this.menu.addEventListener('click', (event) => {
      const target = event.target as HTMLElement
      if (target === this.menu || target.closest('[data-menu-close]')) this.menu.close()
    })

    $$<HTMLButtonElement>('[data-lang]').forEach((button) =>
      button.addEventListener('click', () => store.set({ lang: button.dataset.lang as 'en' | 'me' | 'ru' })),
    )

    this.observeSections()
  }

  update(y: number): void {
    const scrolled = y > 24
    if (scrolled !== this.scrolled) {
      this.scrolled = scrolled
      this.el.classList.toggle('is-scrolled', scrolled)
    }
  }

  syncLanguage(lang: string): void {
    $$<HTMLButtonElement>('[data-lang]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.lang === lang)))
  }

  private onAnchorClick(event: MouseEvent): void {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return
    const link = (event.target as HTMLElement).closest<HTMLAnchorElement>('a[href^="#"]')
    if (!link) return
    const id = link.getAttribute('href')!.slice(1)
    const target = id ? document.getElementById(id) : null
    if (!target) return
    event.preventDefault()
    if (this.menu.open) this.menu.close()
    if (target.id === 'main') {
      target.setAttribute('tabindex', '-1')
      target.focus({ preventScroll: true })
    }
    const stopTop = this.getSectionStop ? this.getSectionStop(id) : undefined
    if (typeof stopTop === 'number') {
      this.scroller.to(stopTop)
    } else {
      this.scroller.to(target.id === 'home' ? 0 : target, 0)
    }
    history.replaceState(null, '', `#${id}`)
  }

  private observeSections(): void {
    const links = $$<HTMLAnchorElement>('[data-nav]')
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          const id = (entry.target as HTMLElement).dataset.section
          links.forEach((link) => {
            const active = link.dataset.nav === id
            link.classList.toggle('is-active', active)
            if (active) link.setAttribute('aria-current', 'location')
            else link.removeAttribute('aria-current')
          })
        }
      },
      { rootMargin: '-45% 0px -50% 0px' },
    )
    $$('[data-section]').forEach((section) => observer.observe(section))
  }
}
